import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Camera, Plus, Trash2, Check, Upload, Image, Car, Home, Loader2, Coins, Heart, Navigation, MapPin, CheckCircle, Map } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { SPOKEN_LANGUAGES } from '@/i18n';
import LocationPicker from './LocationPicker';

// Helper to get API URL - uses current origin in production
const getApiUrl = () => {
  if (typeof window !== 'undefined' && window.location.origin.includes('xperience-guides.com')) {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};
const API_URL = getApiUrl() + '/api';

const ExplorerProfileModal = ({ open, onClose, user, onSuccess }) => {
  const { i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [extraServices, setExtraServices] = useState([]);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [geocodingAddress, setGeocodingAddress] = useState(false);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    bio: '',
    location: '',
    latitude: null,
    longitude: null,
    languages: [],
    specialties: [],
    years_experience: '',
    certifications: [],
    availability: '',
    response_time: '',
    phone: '',
    social_links: {
      instagram: '',
      facebook: '',
      website: ''
    },
    picture: '',
    guide_type: 'pending' // 'retribution' (paid) or 'passion' (free)
  });

  const [newCertification, setNewCertification] = useState('');

  const specialtyOptions = [
    { id: 'adventure', label: i18n.language === 'fr' ? 'Aventure' : 'Adventure' },
    { id: 'nature', label: 'Nature' },
    { id: 'fun', label: 'Fun' },
    { id: 'food', label: i18n.language === 'fr' ? 'Gastronomie' : 'Food & Culinary' },
    { id: 'culture', label: i18n.language === 'fr' ? 'Culture & Histoire' : 'Culture & History' },
    { id: 'sport', label: i18n.language === 'fr' ? 'Sport' : 'Sports' },
    { id: 'healing', label: i18n.language === 'fr' ? 'Bien-être' : 'Wellness & Healing' },
    { id: 'nightlife', label: i18n.language === 'fr' ? 'Vie nocturne' : 'Nightlife' },
    { id: 'vanlife', label: 'Vanlife' },
    { id: 'artisanat', label: i18n.language === 'fr' ? 'Artisanat' : 'Crafts' },
    { id: 'local_events', label: i18n.language === 'fr' ? 'Événements locaux' : 'Local Events' }
  ];

  const responseTimeOptions = [
    { value: 'within_hour', label: i18n.language === 'fr' ? 'Moins d\'une heure' : 'Within an hour' },
    { value: 'within_day', label: i18n.language === 'fr' ? 'Dans la journée' : 'Within a day' },
    { value: 'within_2days', label: i18n.language === 'fr' ? 'Sous 2 jours' : 'Within 2 days' }
  ];

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        bio: user.bio || '',
        location: user.location || '',
        latitude: user.latitude || null,
        longitude: user.longitude || null,
        languages: user.languages || [],
        specialties: user.specialties || [],
        years_experience: user.years_experience || '',
        certifications: user.certifications || [],
        availability: user.availability || '',
        response_time: user.response_time || '',
        phone: user.phone || '',
        social_links: user.social_links || { instagram: '', facebook: '', website: '' },
        picture: user.picture || '',
        guide_type: user.guide_type || 'pending'
      });
      setGalleryPhotos(user.gallery_photos || []);
      setExtraServices(user.extra_services || []);
    }
  }, [user]);

  // Get current geolocation from browser
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error(i18n.language === 'fr' 
        ? 'Géolocalisation non supportée par votre navigateur' 
        : 'Geolocation not supported by your browser');
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        setFormData(prev => ({
          ...prev,
          latitude: lat,
          longitude: lng
        }));
        
        // Try to reverse geocode to get city name
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10&addressdetails=1`,
            { headers: { 'Accept-Language': i18n.language } }
          );
          const data = await response.json();
          
          if (data.address) {
            const city = data.address.city || data.address.town || data.address.village || data.address.municipality || '';
            const country = data.address.country || '';
            const locationText = city && country ? `${city}, ${country}` : data.display_name?.split(',').slice(0, 2).join(',');
            
            setFormData(prev => ({
              ...prev,
              location: locationText
            }));
          }
        } catch (error) {
          console.error('Reverse geocoding failed:', error);
        }
        
        setGettingLocation(false);
        toast.success(i18n.language === 'fr' 
          ? 'Position mise à jour !' 
          : 'Location updated!');
      },
      (error) => {
        setGettingLocation(false);
        let errorMsg = i18n.language === 'fr' 
          ? 'Impossible d\'obtenir votre position' 
          : 'Unable to get your location';
        
        if (error.code === 1) {
          errorMsg = i18n.language === 'fr' 
            ? 'Permission de géolocalisation refusée' 
            : 'Geolocation permission denied';
        }
        toast.error(errorMsg);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Geocode an address text to coordinates
  const geocodeAddress = async () => {
    if (!formData.location.trim()) {
      toast.error(i18n.language === 'fr' 
        ? 'Veuillez entrer une adresse' 
        : 'Please enter an address');
      return;
    }

    setGeocodingAddress(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(formData.location)}&limit=1`,
        { headers: { 'Accept-Language': i18n.language } }
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        const result = data[0];
        setFormData(prev => ({
          ...prev,
          latitude: parseFloat(result.lat),
          longitude: parseFloat(result.lon),
          location: result.display_name?.split(',').slice(0, 3).join(',').trim() || prev.location
        }));
        toast.success(i18n.language === 'fr' 
          ? 'Adresse géolocalisée !' 
          : 'Address geolocated!');
      } else {
        toast.error(i18n.language === 'fr' 
          ? 'Adresse non trouvée. Essayez un format différent.' 
          : 'Address not found. Try a different format.');
      }
    } catch (error) {
      toast.error(i18n.language === 'fr' 
        ? 'Erreur lors de la géolocalisation' 
        : 'Geolocation error');
    } finally {
      setGeocodingAddress(false);
    }
  };

  // Photo upload handler
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (galleryPhotos.length >= 10) {
      toast.error(i18n.language === 'fr' ? 'Maximum 10 photos autorisées' : 'Maximum 10 photos allowed');
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('vgt_token');
      const response = await axios.post(`${API_URL}/upload/profile-photo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        withCredentials: true
      });

      setGalleryPhotos([...galleryPhotos, response.data.url]);
      toast.success(i18n.language === 'fr' ? 'Photo ajoutée !' : 'Photo added!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Delete photo handler
  const handleDeletePhoto = async (photoUrl) => {
    try {
      const token = localStorage.getItem('vgt_token');
      await axios.delete(`${API_URL}/upload/profile-photo`, {
        params: { photo_url: photoUrl },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setGalleryPhotos(galleryPhotos.filter(p => p !== photoUrl));
      toast.success(i18n.language === 'fr' ? 'Photo supprimée' : 'Photo deleted');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Extra services handlers
  const addExtraService = () => {
    setExtraServices([...extraServices, {
      type: 'vehicle',
      name: '',
      description: '',
      included_in_price: true,
      // Vehicle specific
      vehicle_type: '',
      vehicle_seats: '',
      // Accommodation specific
      accommodation_type: '',
      accommodation_capacity: '',
      accommodation_rooms: '',
      photos: []
    }]);
  };

  const updateExtraService = (index, field, value) => {
    const updated = [...extraServices];
    updated[index] = { ...updated[index], [field]: value };
    setExtraServices(updated);
  };

  const handleServicePhotoUpload = async (index, event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const token = localStorage.getItem('vgt_token');
      const response = await axios.post(`${API_URL}/upload`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });
      
      const updated = [...extraServices];
      const currentPhotos = updated[index].photos || [];
      updated[index].photos = [...currentPhotos, response.data.url];
      setExtraServices(updated);
      toast.success(i18n.language === 'fr' ? 'Photo ajoutée' : 'Photo added');
    } catch (error) {
      toast.error(i18n.language === 'fr' ? 'Erreur lors de l\'upload' : 'Upload failed');
    }
  };

  const removeServicePhoto = (serviceIndex, photoIndex) => {
    const updated = [...extraServices];
    updated[serviceIndex].photos = updated[serviceIndex].photos.filter((_, i) => i !== photoIndex);
    setExtraServices(updated);
  };

  const removeExtraService = (index) => {
    setExtraServices(extraServices.filter((_, i) => i !== index));
  };

  const toggleLanguage = (langCode) => {
    if (formData.languages.includes(langCode)) {
      setFormData({
        ...formData,
        languages: formData.languages.filter(l => l !== langCode)
      });
    } else {
      setFormData({
        ...formData,
        languages: [...formData.languages, langCode]
      });
    }
  };

  const handleAddCertification = () => {
    if (newCertification.trim() && !formData.certifications.includes(newCertification.trim())) {
      setFormData({
        ...formData,
        certifications: [...formData.certifications, newCertification.trim()]
      });
      setNewCertification('');
    }
  };

  const handleRemoveCertification = (cert) => {
    setFormData({
      ...formData,
      certifications: formData.certifications.filter(c => c !== cert)
    });
  };

  const toggleSpecialty = (specId) => {
    if (formData.specialties.includes(specId)) {
      setFormData({
        ...formData,
        specialties: formData.specialties.filter(s => s !== specId)
      });
    } else {
      setFormData({
        ...formData,
        specialties: [...formData.specialties, specId]
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation des champs obligatoires
    const requiredFields = [];
    if (!formData.name?.trim()) requiredFields.push(i18n.language === 'fr' ? 'Nom' : 'Name');
    if (!formData.location?.trim()) requiredFields.push(i18n.language === 'fr' ? 'Localisation' : 'Location');
    if (!formData.bio?.trim()) requiredFields.push(i18n.language === 'fr' ? 'Bio' : 'Bio');
    if (!formData.languages || formData.languages.length === 0) requiredFields.push(i18n.language === 'fr' ? 'Langues parlées' : 'Languages');
    if (!formData.specialties || formData.specialties.length === 0) requiredFields.push(i18n.language === 'fr' ? 'Spécialités' : 'Specialties');
    
    if (requiredFields.length > 0) {
      toast.error(
        i18n.language === 'fr' 
          ? `Veuillez remplir les champs obligatoires : ${requiredFields.join(', ')}`
          : `Please fill in required fields: ${requiredFields.join(', ')}`
      );
      return;
    }

    // Validation de la longueur de la bio
    if (formData.bio.trim().length < 50) {
      toast.error(
        i18n.language === 'fr' 
          ? 'Votre bio doit contenir au moins 50 caractères pour mieux vous présenter'
          : 'Your bio must be at least 50 characters to better introduce yourself'
      );
      return;
    }
    
    setLoading(true);

    try {
      const token = localStorage.getItem('vgt_token');
      const payload = {
        ...formData,
        years_experience: formData.years_experience ? parseInt(formData.years_experience) : null,
        latitude: formData.latitude,
        longitude: formData.longitude,
        gallery_photos: galleryPhotos,
        extra_services: extraServices.filter(s => s.name.trim() !== '')
      };

      await axios.put(`${API_URL}/explorer/profile`, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });

      toast.success(i18n.language === 'fr' ? 'Profil mis à jour !' : 'Profile updated!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  // Calcul du pourcentage de complétion du profil
  const getProfileCompletion = () => {
    let completed = 0;
    const total = 8;
    
    if (formData.name?.trim()) completed++;
    if (formData.location?.trim()) completed++;
    if (formData.bio?.trim() && formData.bio.length >= 50) completed++;
    if (formData.languages?.length > 0) completed++;
    if (formData.specialties?.length > 0) completed++;
    if (formData.picture) completed++;
    if (galleryPhotos.length > 0) completed++;
    if (formData.phone?.trim()) completed++;
    
    return Math.round((completed / total) * 100);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            {i18n.language === 'fr' ? 'Compléter mon profil d\'explorateur' : 'Complete my explorer profile'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Profile Completion Progress */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">
                {i18n.language === 'fr' ? 'Complétion du profil' : 'Profile completion'}
              </span>
              <span className={`text-sm font-bold ${getProfileCompletion() === 100 ? 'text-green-600' : 'text-amber-600'}`}>
                {getProfileCompletion()}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all ${getProfileCompletion() === 100 ? 'bg-green-500' : 'bg-amber-500'}`}
                style={{ width: `${getProfileCompletion()}%` }}
              />
            </div>
            {getProfileCompletion() < 100 && (
              <p className="text-xs text-amber-600 mt-2">
                {i18n.language === 'fr' 
                  ? 'Complétez votre profil pour augmenter votre visibilité !'
                  : 'Complete your profile to increase your visibility!'}
              </p>
            )}
          </div>

          {/* Required Fields Note */}
          <p className="text-xs text-gray-500">
            {i18n.language === 'fr' 
              ? '* Champs obligatoires'
              : '* Required fields'}
          </p>

          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg border-b pb-2">
              {i18n.language === 'fr' ? 'Informations de base' : 'Basic Information'}
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">
                  {i18n.language === 'fr' ? 'Nom complet *' : 'Full name *'}
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="profile-name"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">
                  {i18n.language === 'fr' ? 'Localisation *' : 'Location *'}
                </label>
                <div className="flex gap-2">
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value, latitude: null, longitude: null })}
                    placeholder="Paris, France"
                    required
                    data-testid="profile-location"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={geocodeAddress}
                    disabled={geocodingAddress || !formData.location.trim()}
                    title={i18n.language === 'fr' ? 'Géolocaliser cette adresse' : 'Geolocate this address'}
                  >
                    {geocodingAddress ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <MapPin className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                
                {/* Geolocation section */}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={getCurrentLocation}
                    disabled={gettingLocation}
                    className="text-xs"
                  >
                    {gettingLocation ? (
                      <>
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        {i18n.language === 'fr' ? 'Localisation...' : 'Getting location...'}
                      </>
                    ) : (
                      <>
                        <Navigation className="w-3 h-3 mr-1" />
                        {i18n.language === 'fr' ? 'Ma position' : 'My location'}
                      </>
                    )}
                  </Button>
                  
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowMapPicker(true)}
                    className="text-xs bg-primary/5 hover:bg-primary/10 border-primary/30"
                  >
                    <Map className="w-3 h-3 mr-1" />
                    {i18n.language === 'fr' ? 'Choisir sur la carte' : 'Choose on map'}
                  </Button>
                  
                  {formData.latitude && formData.longitude && (
                    <div className="flex items-center gap-1 text-xs text-green-600">
                      <CheckCircle className="w-3 h-3" />
                      <span>{i18n.language === 'fr' ? 'Position validée' : 'Position validated'}</span>
                    </div>
                  )}
                </div>
                
                {formData.latitude && formData.longitude && (
                  <p className="text-xs text-gray-500 mt-1">
                    📍 {formData.latitude.toFixed(4)}, {formData.longitude.toFixed(4)}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-3 block">
                {i18n.language === 'fr' ? 'Photo de profil' : 'Profile photo'}
              </label>
              
              {/* Photo upload section */}
              <div className="flex items-center gap-6">
                {/* Current photo preview */}
                <div className="relative">
                  <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-100 border-4 border-white shadow-lg">
                    {formData.picture ? (
                      <img 
                        src={formData.picture} 
                        alt="Profile" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-orange-100 to-orange-200">
                        <Camera className="w-8 h-8 text-orange-400" />
                      </div>
                    )}
                  </div>
                  {uploadingPhoto && (
                    <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                      <Loader2 className="w-6 h-6 text-white animate-spin" />
                    </div>
                  )}
                </div>
                
                {/* Upload controls */}
                <div className="flex flex-col gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      
                      if (!file.type.startsWith('image/')) {
                        toast.error('Veuillez sélectionner une image');
                        return;
                      }
                      if (file.size > 5 * 1024 * 1024) {
                        toast.error('L\'image ne doit pas dépasser 5MB');
                        return;
                      }
                      
                      setUploadingPhoto(true);
                      try {
                        const uploadFormData = new FormData();
                        uploadFormData.append('file', file);
                        
                        const token = localStorage.getItem('token');
                        const response = await fetch(`${API_URL}/upload/avatar`, {
                          method: 'POST',
                          headers: { 'Authorization': `Bearer ${token}` },
                          body: uploadFormData
                        });
                        
                        if (!response.ok) throw new Error('Upload failed');
                        
                        const data = await response.json();
                        setFormData({ ...formData, picture: data.url });
                        toast.success('Photo mise à jour !');
                      } catch (error) {
                        toast.error('Erreur lors de l\'upload');
                      } finally {
                        setUploadingPhoto(false);
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPhoto}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {formData.picture ? 'Changer la photo' : 'Ajouter une photo'}
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    JPG, PNG • Max 5MB
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">
                {i18n.language === 'fr' ? 'Bio / Présentation *' : 'Bio / About me *'}
              </label>
              <Textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                placeholder={i18n.language === 'fr' 
                  ? 'Parlez de vous, de votre passion pour le voyage et votre région...'
                  : 'Tell us about yourself, your passion for travel and your region...'}
                rows={4}
                required
                data-testid="profile-bio"
              />
            </div>
          </div>

          {/* Languages */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg border-b pb-2">
              {i18n.language === 'fr' ? 'Langues parlées *' : 'Languages spoken *'}
            </h3>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {SPOKEN_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => toggleLanguage(lang.code)}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg border transition-colors ${
                    formData.languages.includes(lang.code)
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white hover:bg-orange-50 border-gray-200'
                  }`}
                  data-testid={`lang-spoken-${lang.code}`}
                >
                  <span className="text-sm">{lang.name}</span>
                  {formData.languages.includes(lang.code) && (
                    <Check className="w-4 h-4" />
                  )}
                </button>
              ))}
            </div>
            
            {formData.languages.length > 0 && (
              <p className="text-sm text-gray-500">
                {i18n.language === 'fr' 
                  ? `${formData.languages.length} langue(s) sélectionnée(s)`
                  : `${formData.languages.length} language(s) selected`}
              </p>
            )}
          </div>

          {/* Specialties */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg border-b pb-2">
              {i18n.language === 'fr' ? 'Spécialités' : 'Specialties'}
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {specialtyOptions.map((spec) => (
                <label
                  key={spec.id}
                  className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    formData.specialties.includes(spec.id)
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-200 hover:border-primary/50'
                  }`}
                >
                  <Checkbox
                    checked={formData.specialties.includes(spec.id)}
                    onCheckedChange={() => toggleSpecialty(spec.id)}
                  />
                  <span className="text-sm">{spec.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Experience & Certifications */}
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-3">
              <h3 className="font-semibold text-lg border-b pb-2">
                {i18n.language === 'fr' ? 'Expérience' : 'Experience'}
              </h3>
              <div>
                <label className="text-sm font-medium mb-1 block">
                  {i18n.language === 'fr' ? 'Années d\'expérience' : 'Years of experience'}
                </label>
                <Input
                  type="number"
                  value={formData.years_experience}
                  onChange={(e) => setFormData({ ...formData, years_experience: e.target.value })}
                  min="0"
                  max="50"
                />
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="font-semibold text-lg border-b pb-2">
                {i18n.language === 'fr' ? 'Certifications' : 'Certifications'}
              </h3>
              <div className="flex gap-2">
                <Input
                  value={newCertification}
                  onChange={(e) => setNewCertification(e.target.value)}
                  placeholder={i18n.language === 'fr' ? 'Guide touristique agréé...' : 'Licensed tour guide...'}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCertification())}
                />
                <Button type="button" onClick={handleAddCertification} variant="outline">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="space-y-1">
                {formData.certifications.map((cert, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-sm">{cert}</span>
                    <button type="button" onClick={() => handleRemoveCertification(cert)} className="text-red-500 hover:text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Availability & Response Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-1 block">
                {i18n.language === 'fr' ? 'Disponibilité' : 'Availability'}
              </label>
              <Input
                value={formData.availability}
                onChange={(e) => setFormData({ ...formData, availability: e.target.value })}
                placeholder={i18n.language === 'fr' ? 'Week-ends, vacances...' : 'Weekends, holidays...'}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">
                {i18n.language === 'fr' ? 'Temps de réponse' : 'Response time'}
              </label>
              <select
                value={formData.response_time}
                onChange={(e) => setFormData({ ...formData, response_time: e.target.value })}
                className="w-full p-2 border rounded-lg"
              >
                <option value="">{i18n.language === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                {responseTimeOptions.map(opt => (
                  <option key={opt.value} value={opt.label}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Contact & Social */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg border-b pb-2">
              {i18n.language === 'fr' ? 'Contact & Réseaux sociaux' : 'Contact & Social Media'}
            </h3>
            
            <div>
              <label className="text-sm font-medium mb-1 block">
                {i18n.language === 'fr' ? 'Téléphone' : 'Phone'}
              </label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+33 6 12 34 56 78"
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-sm font-medium mb-1 block">Instagram</label>
                <Input
                  value={formData.social_links.instagram}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    social_links: { ...formData.social_links, instagram: e.target.value }
                  })}
                  placeholder="https://instagram.com/..."
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Facebook</label>
                <Input
                  value={formData.social_links.facebook}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    social_links: { ...formData.social_links, facebook: e.target.value }
                  })}
                  placeholder="https://facebook.com/..."
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">
                  {i18n.language === 'fr' ? 'Site web' : 'Website'}
                </label>
                <Input
                  value={formData.social_links.website}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    social_links: { ...formData.social_links, website: e.target.value }
                  })}
                  placeholder="https://..."
                />
              </div>
            </div>
          </div>

          {/* Gallery Photos */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg border-b pb-2 flex items-center gap-2">
              <Image className="w-5 h-5 text-primary" />
              {i18n.language === 'fr' ? 'Galerie photos' : 'Photo Gallery'}
              <span className="text-sm font-normal text-gray-500">({galleryPhotos.length}/10)</span>
            </h3>
            
            <div className="grid grid-cols-5 gap-3">
              {galleryPhotos.map((photo, index) => (
                <div key={index} className="relative group aspect-square rounded-lg overflow-hidden border">
                  <img 
                    src={photo.startsWith('/') ? (window.location.origin.includes('xperience-guides.com') ? window.location.origin : process.env.REACT_APP_BACKEND_URL) + photo : photo} 
                    alt={`Gallery ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => handleDeletePhoto(photo)}
                    className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
              
              {galleryPhotos.length < 10 && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="aspect-square rounded-lg border-2 border-dashed border-gray-300 hover:border-primary flex flex-col items-center justify-center gap-1 text-gray-400 hover:text-primary transition-colors"
                >
                  {uploadingPhoto ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    <>
                      <Upload className="w-6 h-6" />
                      <span className="text-xs">{i18n.language === 'fr' ? 'Ajouter' : 'Add'}</span>
                    </>
                  )}
                </button>
              )}
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              className="hidden"
            />
            
            <p className="text-xs text-gray-500">
              {i18n.language === 'fr' 
                ? 'Ajoutez jusqu\'à 10 photos pour montrer vos expériences, votre région, etc.'
                : 'Add up to 10 photos to showcase your experiences, region, etc.'}
            </p>
          </div>

          {/* Extra Services */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg border-b pb-2 flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Car className="w-5 h-5 text-primary" />
                {i18n.language === 'fr' ? 'Services additionnels' : 'Extra Services'}
              </span>
              <Button type="button" variant="outline" size="sm" onClick={addExtraService}>
                <Plus className="w-4 h-4 mr-1" />
                {i18n.language === 'fr' ? 'Ajouter' : 'Add'}
              </Button>
            </h3>

            {extraServices.length === 0 ? (
              <p className="text-sm text-gray-500 italic">
                {i18n.language === 'fr' 
                  ? 'Proposez-vous un véhicule, un hébergement, du matériel ? Ajoutez vos services ici.'
                  : 'Do you offer a vehicle, accommodation, equipment? Add your services here.'}
              </p>
            ) : (
              <div className="space-y-4">
                {extraServices.map((service, index) => (
                  <div key={index} className="p-4 border rounded-lg bg-gray-50 space-y-4">
                    {/* Header with type and delete */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <select
                          value={service.type}
                          onChange={(e) => updateExtraService(index, 'type', e.target.value)}
                          className="p-2 border rounded-lg text-sm font-medium"
                        >
                          <option value="vehicle">{i18n.language === 'fr' ? '🚗 Véhicule' : '🚗 Vehicle'}</option>
                          <option value="accommodation">{i18n.language === 'fr' ? '🏠 Hébergement' : '🏠 Accommodation'}</option>
                          <option value="equipment">{i18n.language === 'fr' ? '🎿 Équipement' : '🎿 Equipment'}</option>
                          <option value="food">{i18n.language === 'fr' ? '🍽️ Repas' : '🍽️ Meals'}</option>
                          <option value="other">{i18n.language === 'fr' ? '📦 Autre' : '📦 Other'}</option>
                        </select>
                        <label className="flex items-center gap-2 text-sm">
                          <Checkbox
                            checked={service.included_in_price}
                            onCheckedChange={(checked) => updateExtraService(index, 'included_in_price', checked)}
                          />
                          {i18n.language === 'fr' ? 'Inclus dans le prix' : 'Included in price'}
                        </label>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeExtraService(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    
                    {/* Vehicle specific fields */}
                    {service.type === 'vehicle' && (
                      <div className="grid grid-cols-2 gap-3 p-3 bg-blue-50 rounded-lg">
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">
                            {i18n.language === 'fr' ? 'Type de véhicule' : 'Vehicle type'}
                          </label>
                          <select
                            value={service.vehicle_type || ''}
                            onChange={(e) => updateExtraService(index, 'vehicle_type', e.target.value)}
                            className="w-full p-2 border rounded-lg text-sm"
                          >
                            <option value="">{i18n.language === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                            <option value="car">{i18n.language === 'fr' ? 'Voiture' : 'Car'}</option>
                            <option value="suv">SUV / 4x4</option>
                            <option value="van">{i18n.language === 'fr' ? 'Van / Minibus' : 'Van / Minibus'}</option>
                            <option value="camper">{i18n.language === 'fr' ? 'Camping-car' : 'Camper van'}</option>
                            <option value="motorcycle">{i18n.language === 'fr' ? 'Moto' : 'Motorcycle'}</option>
                            <option value="bike">{i18n.language === 'fr' ? 'Vélo' : 'Bicycle'}</option>
                            <option value="boat">{i18n.language === 'fr' ? 'Bateau' : 'Boat'}</option>
                            <option value="other">{i18n.language === 'fr' ? 'Autre' : 'Other'}</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">
                            {i18n.language === 'fr' ? 'Nombre de places' : 'Number of seats'}
                          </label>
                          <select
                            value={service.vehicle_seats || ''}
                            onChange={(e) => updateExtraService(index, 'vehicle_seats', e.target.value)}
                            className="w-full p-2 border rounded-lg text-sm"
                          >
                            <option value="">{i18n.language === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                            <option value="2">2 {i18n.language === 'fr' ? 'places' : 'seats'}</option>
                            <option value="4">4 {i18n.language === 'fr' ? 'places' : 'seats'}</option>
                            <option value="5">5 {i18n.language === 'fr' ? 'places' : 'seats'}</option>
                            <option value="7">7 {i18n.language === 'fr' ? 'places' : 'seats'}</option>
                            <option value="9">9+ {i18n.language === 'fr' ? 'places' : 'seats'}</option>
                          </select>
                        </div>
                      </div>
                    )}

                    {/* Accommodation specific fields */}
                    {service.type === 'accommodation' && (
                      <div className="grid grid-cols-3 gap-3 p-3 bg-green-50 rounded-lg">
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">
                            {i18n.language === 'fr' ? 'Type de logement' : 'Accommodation type'}
                          </label>
                          <select
                            value={service.accommodation_type || ''}
                            onChange={(e) => updateExtraService(index, 'accommodation_type', e.target.value)}
                            className="w-full p-2 border rounded-lg text-sm"
                          >
                            <option value="">{i18n.language === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                            <option value="room">{i18n.language === 'fr' ? 'Chambre privée' : 'Private room'}</option>
                            <option value="shared_room">{i18n.language === 'fr' ? 'Chambre partagée' : 'Shared room'}</option>
                            <option value="apartment">{i18n.language === 'fr' ? 'Appartement entier' : 'Entire apartment'}</option>
                            <option value="house">{i18n.language === 'fr' ? 'Maison entière' : 'Entire house'}</option>
                            <option value="guesthouse">{i18n.language === 'fr' ? 'Maison d\'hôtes' : 'Guesthouse'}</option>
                            <option value="cabin">{i18n.language === 'fr' ? 'Chalet / Cabane' : 'Cabin / Chalet'}</option>
                            <option value="tent">{i18n.language === 'fr' ? 'Tente / Camping' : 'Tent / Camping'}</option>
                            <option value="other">{i18n.language === 'fr' ? 'Autre' : 'Other'}</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">
                            {i18n.language === 'fr' ? 'Capacité (personnes)' : 'Capacity (guests)'}
                          </label>
                          <select
                            value={service.accommodation_capacity || ''}
                            onChange={(e) => updateExtraService(index, 'accommodation_capacity', e.target.value)}
                            className="w-full p-2 border rounded-lg text-sm"
                          >
                            <option value="">{i18n.language === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                            <option value="1">1 {i18n.language === 'fr' ? 'personne' : 'guest'}</option>
                            <option value="2">2 {i18n.language === 'fr' ? 'personnes' : 'guests'}</option>
                            <option value="3">3 {i18n.language === 'fr' ? 'personnes' : 'guests'}</option>
                            <option value="4">4 {i18n.language === 'fr' ? 'personnes' : 'guests'}</option>
                            <option value="5">5 {i18n.language === 'fr' ? 'personnes' : 'guests'}</option>
                            <option value="6">6+ {i18n.language === 'fr' ? 'personnes' : 'guests'}</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">
                            {i18n.language === 'fr' ? 'Nombre de chambres' : 'Bedrooms'}
                          </label>
                          <select
                            value={service.accommodation_rooms || ''}
                            onChange={(e) => updateExtraService(index, 'accommodation_rooms', e.target.value)}
                            className="w-full p-2 border rounded-lg text-sm"
                          >
                            <option value="">{i18n.language === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                            <option value="studio">Studio</option>
                            <option value="1">1 {i18n.language === 'fr' ? 'chambre' : 'bedroom'}</option>
                            <option value="2">2 {i18n.language === 'fr' ? 'chambres' : 'bedrooms'}</option>
                            <option value="3">3 {i18n.language === 'fr' ? 'chambres' : 'bedrooms'}</option>
                            <option value="4">4+ {i18n.language === 'fr' ? 'chambres' : 'bedrooms'}</option>
                          </select>
                        </div>
                      </div>
                    )}
                    
                    {/* Service name */}
                    <Input
                      value={service.name}
                      onChange={(e) => updateExtraService(index, 'name', e.target.value)}
                      placeholder={
                        service.type === 'vehicle' 
                          ? (i18n.language === 'fr' ? 'Ex: Land Rover Defender 110' : 'E.g. Land Rover Defender 110')
                          : service.type === 'accommodation'
                            ? (i18n.language === 'fr' ? 'Ex: Appartement vue mer à Nice' : 'E.g. Sea view apartment in Nice')
                            : (i18n.language === 'fr' ? 'Nom du service' : 'Service name')
                      }
                    />
                    
                    {/* Description */}
                    <Textarea
                      value={service.description}
                      onChange={(e) => updateExtraService(index, 'description', e.target.value)}
                      placeholder={i18n.language === 'fr' ? 'Description détaillée (équipements, commodités, etc.)' : 'Detailed description (amenities, features, etc.)'}
                      rows={2}
                    />

                    {/* Photos for vehicle and accommodation */}
                    {(service.type === 'vehicle' || service.type === 'accommodation') && (
                      <div className="space-y-2">
                        <label className="text-xs font-medium text-gray-600 flex items-center gap-1">
                          <Camera className="w-3 h-3" />
                          {i18n.language === 'fr' ? 'Photos (optionnel)' : 'Photos (optional)'}
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {(service.photos || []).map((photo, photoIndex) => (
                            <div key={photoIndex} className="relative w-16 h-16 rounded-lg overflow-hidden border group">
                              <img 
                                src={photo.startsWith('/') ? (window.location.origin.includes('xperience-guides.com') ? window.location.origin : process.env.REACT_APP_BACKEND_URL) + photo : photo} 
                                alt={`Service ${photoIndex + 1}`}
                                className="w-full h-full object-cover"
                              />
                              <button
                                type="button"
                                onClick={() => removeServicePhoto(index, photoIndex)}
                                className="absolute top-0 right-0 p-0.5 bg-red-500 text-white rounded-bl opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                          {(service.photos || []).length < 5 && (
                            <label className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 hover:border-primary flex items-center justify-center cursor-pointer transition-colors">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleServicePhotoUpload(index, e)}
                                className="hidden"
                              />
                              <Plus className="w-5 h-5 text-gray-400" />
                            </label>
                          )}
                        </div>
                        <p className="text-xs text-gray-400">
                          {i18n.language === 'fr' ? 'Max 5 photos par service' : 'Max 5 photos per service'}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button type="submit" disabled={loading} data-testid="save-profile-btn">
              {loading 
                ? (i18n.language === 'fr' ? 'Enregistrement...' : 'Saving...') 
                : (i18n.language === 'fr' ? 'Enregistrer' : 'Save')}
            </Button>
          </div>
        </form>
      </DialogContent>

      {/* Location Picker Modal */}
      {showMapPicker && (
        <LocationPicker
          initialLat={formData.latitude}
          initialLng={formData.longitude}
          initialAddress={formData.location}
          onLocationChange={(locationData) => {
            setFormData(prev => ({
              ...prev,
              latitude: locationData.latitude,
              longitude: locationData.longitude,
              location: locationData.address || prev.location
            }));
            toast.success(i18n.language === 'fr' ? 'Position mise à jour !' : 'Location updated!');
          }}
          onClose={() => setShowMapPicker(false)}
        />
      )}
    </Dialog>
  );
};

export default ExplorerProfileModal;
